<template>
  <v-dialog
    :value="value"
    :fullscreen="isMobile"
    max-width="680"
    scrollable
    @input="$emit('input', $event)"
  >
    <div class="cf">
      <header class="cf__head">
        <span class="cf__icon"><v-icon color="primary">{{ mode === 'convert' ? 'mdi-swap-horizontal' : 'mdi-briefcase-plus-outline' }}</v-icon></span>
        <div class="cf__titles">
          <div class="cf__title">{{ title }}</div>
          <div class="cf__sub">{{ mode === 'convert' ? $t('crmConvertHint') : $t('crmDealFormHint') }}</div>
        </div>
        <button type="button" class="cf__close" @click="close"><v-icon>mdi-close</v-icon></button>
      </header>

      <div class="cf__body">
        <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>

        <div v-if="mode === 'convert'" class="cf__switch">
          <v-switch v-model="createDeal" inset hide-details :label="$t('crmConvertCreateDeal')" />
          <v-switch v-model="createAccount" inset hide-details :label="$t('crmConvertToCustomer')" />
        </div>

        <div v-if="mode !== 'convert' || createDeal" class="cf__grid">
          <v-text-field v-model="f.title" :label="$t('crmDealTitle') + ' *'" outlined dense hide-details="auto" class="cf__full" :error-messages="fe.title" autofocus />
          <v-select v-model="f.pipeline_id" :items="pipelineItems" :label="$t('crmPipeline')" outlined dense hide-details="auto" @change="f.stage_id = null" />
          <v-select v-model="f.stage_id" :items="stageItems" :label="$t('crmStage')" outlined dense hide-details="auto" clearable :placeholder="$t('crmFirstOpenStage')" />
          <v-text-field
            v-if="canFinancial"
            v-model="amountText"
            :label="$t('crmAmount')"
            outlined dense hide-details="auto" dir="ltr"
            :error-messages="fe.amount"
            :hint="amountWords"
            persistent-hint
          />
          <div v-else class="cf__locked"><v-icon small>mdi-lock-outline</v-icon> {{ $t('crmAmountHidden') }}</div>
          <date-picker
            v-model="f.expected_close_date"
            format="YYYY-MM-DD"
            display-format="jYYYY/jMM/jDD"
            :locale="dateLocale"
            :placeholder="$t('crmExpectedClose')"
            clearable
          />
          <template v-if="mode !== 'convert'">
            <v-select v-model="f.priority" :items="priorityItems" :label="$t('crmPriority')" outlined dense hide-details="auto" />
            <crm-user-picker v-model="f.owner_user_id" title="crmOwner" :disabled="!canAssign" />
            <div class="cf__slider cf__full">
              <span>{{ $t('crmProbability') }}</span>
              <v-slider v-model="f.probability" min="0" max="100" step="5" thumb-label hide-details />
              <b>{{ f.probability }}٪</b>
            </div>
            <v-combobox v-model="f.tags" :label="$t('crmTags')" outlined dense hide-details multiple small-chips deletable-chips class="cf__full" />
            <v-textarea v-model="f.notes" :label="$t('crmNotes')" outlined dense hide-details rows="2" auto-grow class="cf__full" />
          </template>
        </div>
      </div>

      <footer class="cf__foot">
        <v-btn color="primary" depressed class="app-form-action-btn" :loading="saving" @click="save">
          {{ mode === 'convert' ? $t('crmConvert') : $t('crmSave') }}
        </v-btn>
        <v-btn color="grey" outlined class="app-form-action-btn" @click="close">{{ $t('crmCancel') }}</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import CrmUserPicker from './CrmUserPicker'
import api, { crmError } from '../crmApi'
import { crm, can, memberOptions, PRIORITIES, fullName, money } from '../crmStore'

export default {
  name: 'CrmDealForm',
  components: { CrmUserPicker },
  props: {
    value: Boolean,
    deal: { type: Object, default: null },
    lead: { type: Object, default: null }, // convert mode
    pipelineId: { type: Number, default: null },
    stageId: { type: Number, default: null }
  },
  data: () => ({ f: {}, fe: {}, error: '', saving: false, createDeal: true, createAccount: false }),
  computed: {
    mode () { return this.lead ? 'convert' : (this.deal ? 'edit' : 'create') },
    title () {
      if (this.mode === 'convert') return this.$t('crmConvertLead')
      return this.deal ? this.$t('crmEditDeal') : this.$t('crmNewDeal')
    },
    pipelineItems () { return crm.pipelines.map(p => ({ value: p.id, text: p.title })) },
    stageItems () {
      const p = crm.pipelines.find(x => x.id === this.f.pipeline_id)
      return p ? (p.stages || []).filter(s => this.deal || s.kind === 'open').map(s => ({ value: s.id, text: s.title })) : []
    },
    ownerItems () { return memberOptions() },
    priorityItems () { return PRIORITIES.map(p => ({ value: p, text: this.$t('crmPriority_' + p) })) },
    canFinancial () { return can('deals.financial') },
    canAssign () { return can('deals.assign') },
    dateLocale () { return this.$i18n.locale === 'en' ? 'en' : 'fa' },
    amountText: {
      get () { return this.f.amount ? Number(this.f.amount).toLocaleString('en-US') : '' },
      set (v) {
        const n = String(v || '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[^\d]/g, '')
        this.f.amount = n ? Number(n) : null
      }
    },
    amountWords () { return this.f.amount ? money(this.f.amount, true) : '' }
  },
  watch: { value: { immediate: true, handler (v) { if (v) this.reset() } } },
  methods: {
    reset () {
      const d = this.deal || {}
      const l = this.lead
      const pipelineId = d.pipeline_id || this.pipelineId || (crm.pipelines[0] && crm.pipelines[0].id) || null
      this.f = {
        title: d.title || (l ? (l.need || l.company_name || fullName(l)) : ''),
        pipeline_id: pipelineId,
        stage_id: d.stage_id || this.stageId || null,
        amount: d.amount || null,
        expected_close_date: d.expected_close_date || '',
        priority: d.priority || 'normal',
        owner_user_id: d.owner_user_id || (crm.me ? crm.me.user_id : null),
        probability: d.probability || 0,
        tags: Array.isArray(d.tags) ? d.tags.slice() : [],
        notes: d.notes || ''
      }
      this.createDeal = true
      this.createAccount = false
      this.fe = {}
      this.error = ''
    },
    close () { this.$emit('input', false) },
    async save () {
      const needDeal = this.mode !== 'convert' || this.createDeal
      if (needDeal && !this.f.title) { this.fe = { title: this.$t('crmRequired') }; return }
      this.saving = true
      this.error = ''
      this.fe = {}
      const body = { ...this.f }
      if (!body.expected_close_date) body.expected_close_date = null
      if (!this.canFinancial) delete body.amount
      try {
        let res
        if (this.mode === 'convert') {
          res = await api.convertLead(this.lead.id, {
            create_deal: this.createDeal,
            to_customer: this.createAccount,
            pipeline_id: body.pipeline_id,
            stage_id: body.stage_id,
            title: body.title,
            amount: body.amount,
            expected_close_date: body.expected_close_date
          })
        } else if (this.deal) {
          res = await api.updateDeal(this.deal.id, body)
        } else {
          res = await api.createDeal(body)
        }
        this.$emit('saved', res)
        this.close()
      } catch (e) {
        const err = crmError(e)
        if (err.code === 'required_fields') {
          this.error = this.$t('crmRequiredFieldsMissing') + ': ' + (err.payload.missing_fields || []).map(x => x.label).join('، ')
        } else {
          this.fe = err.fields
          this.error = err.message
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.cf { display: flex; flex-direction: column; background: #fff; border-radius: 6px; max-height: 90vh; overflow: hidden; }
.theme--dark .cf { background: #1e1e1e; }
.cf__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.cf__icon { width: 40px; height: 40px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cf__titles { flex: 1; min-width: 0; }
.cf__title { font-weight: 700; font-size: 1rem; }
.cf__sub { font-size: .78rem; opacity: .6; }
.cf__close { width: 36px; height: 36px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: flex; align-items: center; justify-content: center; }
.cf__body { padding: 20px 18px; overflow-y: auto; overflow-x: hidden; flex: 1; }
.cf__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px 14px; }
.cf__full { grid-column: 1 / -1; }
.cf__switch { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; padding: 12px 14px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; }
.cf__slider { display: flex; align-items: center; gap: 12px; font-size: .82rem; }
.cf__slider span { min-width: 90px; opacity: .7; }
.cf__slider b { min-width: 36px; text-align: end; }
.cf__locked { display: flex; align-items: center; gap: 6px; font-size: .82rem; opacity: .6; border: 1px dashed rgba(127, 127, 127, .4); border-radius: 6px; padding: 0 12px; height: 40px; }
.cf__foot { display: flex; gap: 10px; padding: 12px 18px; background: #fafbfd; border-top: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.theme--dark .cf__foot { background: #232323; }
.cf ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.cf ::v-deep .v-input fieldset { border-radius: 6px; }
@media (max-width: 799px) {
  .cf { max-height: none; height: 100%; border-radius: 0; }
  .cf__grid { grid-template-columns: 1fr; }
}
</style>
